import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  TablePagination,
  LinearProgress,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Assignment as AssignmentIcon,
  Science as ScienceIcon,
  AttachMoney as CostIcon,
  Verified as VerifiedIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Analytics as AnalyticsIcon,
  LocalDrink as LocalDrinkIcon,
  Factory as FactoryIcon,
  Print as PrintIcon,
  GetApp as ExportIcon,
  Summarize as SummarizeIcon,
  Close as CloseIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';
import { convertUnits, getUnitType } from '../../../utils/unitConversions';
import { usageChemicalConfig } from '../../inventory/inventory-components/Usage/usageConfigs/usageChemicalConfig';
import { usePermissions } from '../../../utils/permissions';
import dayjs from 'dayjs';

// Date utility functions to avoid timezone issues
const formatDateForDisplay = (dateString) => {
  if (!dateString) return 'Not set';
  try {
    // Parse as local date to avoid timezone shifts
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return dateString;
  }
};

export default function BatchSummary({ batch, onPrint, onExport, onUpdate, showSnackbar, user }) {
  const theme = useTheme();
  const { canAccessPage, canApproveProductionBatches } = usePermissions();
  const [batchTimeFormat, setBatchTimeFormat] = useState('12hour');
  const [summaryData, setSummaryData] = useState({
    batchIngredients: [],
    batchCosts: null,
    batchLog: null,
    yieldSummary: null,
    qcTests: [],
    signatures: [],
    deviations: [],
    premixChecklist: null,
    formulation: null,
    loggedIngredients: []
  });
  const [formulationIngredients, setFormulationIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalSignature, setApprovalSignature] = useState('');
  const [signaturePreviewName, setSignaturePreviewName] = useState('');
  const [inventoryCheckLoading, setInventoryCheckLoading] = useState(false);
  const [hasEnoughInventory, setHasEnoughInventory] = useState(true);
  const [insufficientInventoryItems, setInsufficientInventoryItems] = useState([]);
  const [unapproveDialogOpen, setUnapproveDialogOpen] = useState(false);
  const [unapproving, setUnapproving] = useState(false);
  const [unapprovalSignature, setUnapprovalSignature] = useState('');
  const [unapprovalReason, setUnapprovalReason] = useState('');
  const [unapprovalSignaturePreview, setUnapprovalSignaturePreview] = useState('');
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [approvalPage, setApprovalPage] = useState(0);
  const [approvalRowsPerPage, setApprovalRowsPerPage] = useState(5);
  const [shouldCreateUsageTransactions, setShouldCreateUsageTransactions] = useState(true);
  const [accordionState, setAccordionState] = useState({
    metrics: false,
    ingredients: false,
    costs: false,
    production: false,
    densityDetails: false,
    qcTests: false,
    yield: false,
    deviations: false,
    approvalHistory: false
  });

  // Load accordion states from localStorage on mount
  useEffect(() => {
    const savedStates = localStorage.getItem(`batchSummary_accordions_${batch?.id || 'default'}`);
    if (savedStates) {
      try {
        const parsedStates = JSON.parse(savedStates);
        setAccordionState(parsedStates);
      } catch (error) {
        console.warn('Failed to parse saved accordion states:', error);
      }
    }
  }, [batch?.id]);

  // Save accordion states to localStorage when they change
  const updateAccordionState = (accordionKey, expanded) => {
    const newStates = { ...accordionState, [accordionKey]: expanded };
    setAccordionState(newStates);
    localStorage.setItem(`batchSummary_accordions_${batch?.id || 'default'}`, JSON.stringify(newStates));
  };

  // Suppress ResizeObserver errors globally - Fix for accordion intersection issues
  useEffect(() => {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      if (args[0] && args[0].toString().includes('ResizeObserver loop completed with undelivered notifications')) {
        return; // Suppress this specific error
      }
      originalConsoleError.apply(console, args);
    };
    
    // Also handle as window error event
    const handleResizeObserverError = (e) => {
      if (e.message && e.message.includes('ResizeObserver loop completed with undelivered notifications')) {
        e.preventDefault();
        e.stopPropagation();
        return true;
      }
    };
    
    window.addEventListener('error', handleResizeObserverError);
    
    return () => {
      // Restore original console.error
      console.error = originalConsoleError;
      window.removeEventListener('error', handleResizeObserverError);
    };
  }, []);

  // Load user profile for time format preference
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        if (!user?.id) return;
        
        const { data, error } = await supabase
          .from('profiles')
          .select('batch_time_format_preference')
          .eq('id', user.id)
          .single();
          
        if (data && !error) {
          setBatchTimeFormat(data.batch_time_format_preference || '12hour');
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
        setBatchTimeFormat('12hour');
      }
    };
    
    loadUserProfile();
  }, [user?.id]);

  useEffect(() => {
    if (batch?.id && typeof batch.id === 'string') {
      loadSummaryData();
      fetchApprovalHistory();
    }
  }, [batch]);

  const loadSummaryData = async () => {
    if (!batch?.id) return;

    // Ensure batch.id is a string
    const batchId = String(batch.id);
    
    setLoading(true);
    try {
      // First get batch log to get batch_log_id for linking related tables
      const { data: batchLogData, error: batchLogError } = await supabase
        .from('batch_logs')
        .select('*')
        .eq('batch_id', batchId)
        .single();

      const batchLogId = batchLogData?.id;

      // Load all data in parallel
      const [
        ingredientsResult,
        costsResult,
        yieldResult,
        qcResult,
        signaturesResult,
        deviationsResult,
        premixResult,
        productResult,
        formulationResult,
        locationResult,
        formulationIngredientsResult,
        loggedIngredientsResult
      ] = await Promise.all([
        // Batch ingredients
        supabase
          .from('batch_ingredients')
          .select(`
            *,
            raw_materials:raw_material_id (
              id, name, cas_number, sku, category, 
              material_type, primary_unit_measure, color, form,
              density_g_ml, primary_unit_type, primary_unit_quantity
            ),
            formulation_phases:formulation_phase_id (
              id, name, sequence
            )
          `)
          .eq('batch_id', batchId)
          .order('sequence_number'),

        // Batch costs
        supabase
          .from('batch_costs')
          .select('*')
          .eq('batch_id', batchId)
          .single(),

        // Yield summary - fetched by batch_id (specific batch record)
        supabase
          .from('yield_summary')
          .select('*')
          .eq('batch_id', batchId)
          .single(),

        // QC tests from qc_tests table - linked by batch_id
        supabase
          .from('qc_tests')
          .select('*')
          .eq('batch_id', batchId)
          .order('created_at', { ascending: false }),

        // Signatures
        supabase
          .from('batch_signatures')
          .select('*')
          .eq('batch_id', batchId),

        // Deviations from batch_log_deviations - linked by batch_log_id  
        batchLogId ? supabase
          .from('batch_log_deviations')
          .select('*')
          .eq('batch_log_id', batchLogId) : Promise.resolve({ data: [], error: null }),

        // Premix checklist from batch_log_checklists - linked by batch_log_id
        batchLogId ? supabase
          .from('batch_log_checklists')
          .select('*')
          .eq('batch_log_id', batchLogId)
          .single() : Promise.resolve({ data: null, error: null }),

        // Product details
        batch?.product_id ? supabase.from('products').select('*').eq('id', batch.product_id).single() : Promise.resolve({ data: null, error: null }),
        
        // Formulation details
        batch?.formulation_id ? supabase.from('formulations').select('*').eq('id', batch.formulation_id).single() : Promise.resolve({ data: null, error: null }),
        
        // Location details
        batch?.production_location ? supabase.from('locations').select('*').eq('id', batch.production_location).single() : Promise.resolve({ data: null, error: null }),
        
        // Formulation ingredients
        batch?.formulation_id ? supabase
          .from('formulation_ingredients_new')
          .select(`
            *,
            raw_materials:ingredient_id (
              id, name, cas_number, sku, category, 
              material_type, primary_unit_measure, color, form,
              density_g_ml, primary_unit_type, primary_unit_quantity
            )
          `)
          .eq('formulation_id', batch.formulation_id)
          .order('sequence') : Promise.resolve({ data: null, error: null }),

        // Logged ingredients (actual ingredients used in production) with lots
        batchLogId ? supabase
          .from('batch_log_ingredients')
          .select(`
            *,
            batch_log_ingredient_lots (
              id,
              inventory_item_id,
              quantity_used,
              unit_used,
              lot_number,
              batch_number,
              expiry_date,
              manufactured_date,
              location_name,
              notes
            ),
            raw_materials:ingredient_id (
              id,
              primary_unit_measure
            )
          `)
          .eq('batch_log_id', batchLogId)
          .order('addition_order') : Promise.resolve({ data: [], error: null })
      ]);

      setSummaryData({
        batchIngredients: ingredientsResult.data || [],
        batchCosts: costsResult.data,
        batchLog: batchLogData,
        yieldSummary: yieldResult.data,
        qcTests: qcResult.data || [],
        signatures: signaturesResult.data || [],
        deviations: deviationsResult.data || [],
        premixChecklist: premixResult.data,
        product: productResult.data,
        formulation: formulationResult.data,
        location: locationResult.data,
        loggedIngredients: loggedIngredientsResult.data || []
      });
      
      setFormulationIngredients(formulationIngredientsResult.data || []);
    } catch (error) {
      console.error('Error loading summary data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num == null || isNaN(num)) return '0';
    return num.toLocaleString('en-US');
  };
  
  const formatCurrency = (amount) => `$${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatPercentage = (value) => `${(value || 0).toFixed(1)}%`;
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    // Handle timestamps by splitting on 'T' first
    const dateOnly = dateString.includes('T') ? dateString.split('T')[0] : dateString;
    // Use the timezone-safe formatDateForDisplay function
    return formatDateForDisplay(dateOnly);
  };

  const calculateVariance = (planned, plannedUnit, actual, actualUnit, density) => {
    if (!planned || !actual) return null;
    
    try {
      // Convert actual amount to planned unit for comparison
      let actualInPlannedUnits = actual;
      if (plannedUnit !== actualUnit) {
        actualInPlannedUnits = convertUnits(actual, actualUnit, plannedUnit, density);
      }
      
      const variance = actualInPlannedUnits - planned;
      const variancePercentage = (variance / planned) * 100;
      
      return {
        variance,
        variancePercentage,
        actualInPlannedUnits
      };
    } catch (error) {
      console.error('Error calculating variance:', error);
      return null;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleString();
  };
  
  const formatDeviationType = (type) => {
    if (!type) return 'Unknown';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatTestType = (type) => {
    if (!type) return 'Unknown Test';
    return type.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
  };

  const fetchApprovalHistory = async () => {
    try {
      const { data: historyData, error: historyError } = await supabase
        .from('batch_approval_history')
        .select('*')
        .eq('batch_id', batch?.id)
        .order('performed_at', { ascending: false });
        
      if (historyError) throw historyError;
      
      if (!historyData || historyData.length === 0) {
        setApprovalHistory([]);
        return;
      }
      
      const userIds = [...new Set(historyData.map(entry => entry.performed_by).filter(Boolean))];
      
      if (userIds.length === 0) {
        setApprovalHistory(historyData);
        return;
      }
      
      const profilePromises = userIds.map(userId => 
        supabase.rpc('get_profile_by_user_id', { p_user_id: userId })
      );
      
      const profileResults = await Promise.all(profilePromises);
      const profileData = profileResults
        .filter(result => !result.error && result.data && result.data.length > 0)
        .map(result => result.data[0]);
      
      const { data: userEmailData } = await supabase
        .rpc('get_user_emails_for_company', { 
          p_company_id: batch?.company_id 
        });
      
      const enrichedData = historyData.map(entry => {
        const profile = profileData.find(p => p.id === entry.performed_by);
        const userEmail = userEmailData?.find(u => u.user_id === entry.performed_by);
        
        return {
          ...entry,
          profiles: profile || {
            id: entry.performed_by,
            first_name: null,
            last_name: null,
            email: userEmail?.email || null
          }
        };
      });
      
      setApprovalHistory(enrichedData);
    } catch (error) {
      console.error('Error fetching approval history:', error);
      setApprovalHistory([]);
    }
  };

  const checkInventoryAvailability = async (loggedIngredients) => {
    const insufficientItems = [];
    const locationId = batch?.production_location;
    const companyId = batch?.company_id;

    try {
      for (const ingredient of loggedIngredients) {
        const rawMaterialId = ingredient.ingredient_id;
        const actualQuantity = parseFloat(ingredient.actual_quantity) || 0;
        if (!rawMaterialId || actualQuantity <= 0) continue;

        const ingredientLots = ingredient.batch_log_ingredient_lots || [];

        if (ingredientLots.length > 0) {
          for (const lotEntry of ingredientLots) {
            const requiredQty = parseFloat(lotEntry.quantity_used) || 0;
            if (requiredQty <= 0) continue;

            let availableQty = 0;
            if (lotEntry.inventory_item_id) {
              const { data } = await supabase
                .from('raw_materials_inventory_items')
                .select('current_total_quantity')
                .eq('id', lotEntry.inventory_item_id)
                .single();
              availableQty = parseFloat(data?.current_total_quantity) || 0;
            } else {
              let query = supabase
                .from('raw_materials_inventory_items')
                .select('current_total_quantity')
                .eq('raw_material_id', rawMaterialId)
                .eq('location_id', locationId)
                .eq('is_active', true);
              if (lotEntry.lot_number) query = query.eq('lot_number', lotEntry.lot_number);
              if (lotEntry.batch_number) query = query.eq('batch_number', lotEntry.batch_number);
              const { data } = await query.limit(1).single();
              availableQty = parseFloat(data?.current_total_quantity) || 0;
            }

            if (availableQty < requiredQty) {
              insufficientItems.push({
                name: ingredient.ingredient_name,
                required: requiredQty,
                available: availableQty,
                lot: lotEntry.lot_number || lotEntry.batch_number
              });
            }
          }
        } else {
          // No specific lots — check total availability via new config
          const availableItems = await usageChemicalConfig.getInventoryItems(companyId, rawMaterialId, locationId);
          const totalAvailable = availableItems.reduce((sum, item) => sum + (parseFloat(item.current_total_quantity) || 0), 0);
          if (totalAvailable < actualQuantity) {
            insufficientItems.push({
              name: ingredient.ingredient_name,
              required: actualQuantity,
              available: totalAvailable
            });
          }
        }
      }

      return { hasEnoughInventory: insufficientItems.length === 0, insufficientItems };
    } catch (error) {
      console.error('Error checking inventory availability:', error);
      return { hasEnoughInventory: false, insufficientItems: [] };
    }
  };

  // Function to fix missing ingredient_id values in logged ingredients
  const fixMissingIngredientIds = async (loggedIngredients) => {
    const fixedCount = { success: 0, failed: 0 };
    
    for (const ingredient of loggedIngredients) {
      if (!ingredient.ingredient_id && ingredient.batch_ingredient_id) {
        try {
          // Get the raw_material_id from batch_ingredients table
          const { data: batchIngredient, error: fetchError } = await supabase
            .from('batch_ingredients')
            .select('raw_material_id')
            .eq('id', ingredient.batch_ingredient_id)
            .single();

          if (fetchError || !batchIngredient?.raw_material_id) {
            console.error(`Failed to get raw_material_id for ${ingredient.ingredient_name}:`, fetchError);
            fixedCount.failed++;
            continue;
          }

          // Update the logged ingredient with the correct ingredient_id
          const { error: updateError } = await supabase
            .from('batch_log_ingredients')
            .update({ ingredient_id: batchIngredient.raw_material_id })
            .eq('id', ingredient.id);

          if (updateError) {
            console.error(`Failed to update ingredient_id for ${ingredient.ingredient_name}:`, updateError);
            fixedCount.failed++;
          } else {
            // Update the local data too
            ingredient.ingredient_id = batchIngredient.raw_material_id;
            fixedCount.success++;
          }
        } catch (error) {
          console.error(`Error fixing ingredient_id for ${ingredient.ingredient_name}:`, error);
          fixedCount.failed++;
        }
      }
    }
    
    return fixedCount;
  };

  const createUsageTransactions = async (loggedIngredients) => {
    if (!loggedIngredients || loggedIngredients.length === 0) {
      return { success: true, errors: [] };
    }

    const errors = [];
    const locationId = batch?.production_location;
    const companyId = batch?.company_id;

    try {
      for (const ingredient of loggedIngredients) {
        const rawMaterialId = ingredient.ingredient_id;
        const actualQuantity = parseFloat(ingredient.actual_quantity) || 0;
        const materialName = ingredient.ingredient_name || 'Unknown Material';

        if (actualQuantity <= 0) {
          console.warn(`Skipping ${materialName} - zero or negative quantity`);
          continue;
        }

        if (!rawMaterialId) {
          errors.push(`${materialName}: Missing raw material ID. Please re-log this ingredient.`);
          continue;
        }

        // Collect lots — prefer logged lots, fall back to planned lots
        let ingredientLots = ingredient.batch_log_ingredient_lots || [];

        if (ingredientLots.length === 0 && ingredient.batch_ingredient_id) {
          try {
            const { data: plannedLots, error: plannedLotsError } = await supabase
              .from('batch_ingredient_lots')
              .select('*')
              .eq('batch_ingredient_id', ingredient.batch_ingredient_id);

            if (!plannedLotsError && plannedLots?.length > 0) {
              ingredientLots = plannedLots;
            }
          } catch (err) {
            console.error(`Error fetching planned lots for ${materialName}:`, err);
          }
        }

        try {
          if (ingredientLots.length > 0) {
            // Build usages per lot using new config
            // Resolve inventory_item_id for any lots that are missing it
            const resolvedLots = [];
            for (const lot of ingredientLots) {
              const qty = parseFloat(lot.quantity_used) || 0;
              if (qty <= 0) continue;

              let inventoryItemId = lot.inventory_item_id;
              if (!inventoryItemId) {
                let q = supabase
                  .from('raw_materials_inventory_items')
                  .select('id')
                  .eq('raw_material_id', rawMaterialId)
                  .eq('location_id', locationId)
                  .eq('is_active', true);
                if (lot.lot_number) q = q.eq('lot_number', lot.lot_number);
                if (lot.batch_number) q = q.eq('batch_number', lot.batch_number);
                const { data: found } = await q.limit(1).single();
                inventoryItemId = found?.id || null;
              }

              if (!inventoryItemId) {
                errors.push(`${materialName}: Could not resolve inventory item for lot ${lot.lot_number || lot.batch_number || '(no lot)'}`);
                continue;
              }

              resolvedLots.push({
                itemId: rawMaterialId,
                lotId: inventoryItemId,
                quantity: qty,
                lot_number: lot.lot_number,
                batch_number: lot.batch_number,
                quantityMode: 'measure'
              });
            }

            const usages = resolvedLots;

            if (usages.length > 0) {
              await usageChemicalConfig.executeUsage(companyId, {
                locationId,
                usages,
                notes: `Production batch usage — ${materialName} consumed for production batch #${batch?.batch_number}`
              });
            }
          } else {
            // FIFO fallback: deduct across available lots in order
            const availableItems = await usageChemicalConfig.getInventoryItems(companyId, rawMaterialId, locationId);
            let remaining = actualQuantity;
            const fifoUsages = [];

            for (const item of availableItems) {
              if (remaining <= 0) break;
              const available = parseFloat(item.current_total_quantity) || 0;
              const deduct = Math.min(remaining, available);
              if (deduct > 0) {
                fifoUsages.push({
                  itemId: rawMaterialId,
                  lotId: item.id,
                  quantity: parseFloat(deduct.toFixed(4)),
                  lot_number: item.lot_number,
                  batch_number: item.batch_number,
                  quantityMode: 'measure'
                });
                remaining -= deduct;
              }
            }

            if (remaining > 0) {
              errors.push(`${materialName}: Insufficient inventory for FIFO deduction`);
            } else if (fifoUsages.length > 0) {
              await usageChemicalConfig.executeUsage(companyId, {
                locationId,
                usages: fifoUsages,
                notes: `Production batch usage (FIFO) — ${materialName} consumed for production batch #${batch?.batch_number}`
              });
            }
          }
        } catch (err) {
          console.error(`Failed to create usage transaction for ${materialName}:`, err);
          errors.push(`${materialName}: ${err.message}`);
        }
      }

      return { success: errors.length === 0, errors };
    } catch (error) {
      console.error('Error creating usage transactions:', error);
      return { success: false, errors: [`Unexpected error: ${error.message}`] };
    }
  };

  const handlePrintSummary = () => {
    const actualVolume = calculateActualProductionVolume();
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Batch Summary - ${batch?.batch_number || 'Production Batch'}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
              line-height: 1.5;
              color: #1a1a1a;
              background: white;
              padding: 40px;
              font-size: 14px;
            }
            
            .header {
              text-align: center;
              padding-bottom: 32px;
              margin-bottom: 40px;
              border-bottom: 2px solid #e5e5e5;
            }
            
            .header h1 {
              color: #1a1a1a;
              font-size: 32px;
              font-weight: 400;
              margin-bottom: 12px;
              letter-spacing: -0.5px;
            }
            
            .header p {
              color: #6b7280;
              font-size: 16px;
              font-weight: 400;
              margin: 4px 0;
            }
            
            .section {
              margin-bottom: 40px;
              break-inside: avoid;
            }
            
            .section-title {
              background: #f8fafc;
              color: #374151;
              padding: 16px 24px;
              margin-bottom: 24px;
              font-size: 18px;
              font-weight: 400;
              border-left: 4px solid #000000;
              border-radius: 0 8px 8px 0;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }
            
            .info-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
              gap: 20px;
              margin-bottom: 24px;
            }
            
            .info-item {
              background: #ffffff;
              padding: 20px;
              border-radius: 8px;
              border: 1px solid #e5e7eb;
              box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
            }
            
            .info-label {
              font-weight: 400;
              color: #6b7280;
              font-size: 13px;
              margin-bottom: 8px;
              letter-spacing: 0.5px;
            }
            
            .info-value {
              font-size: 16px;
              color: #1f2937;
              font-weight: 400;
              line-height: 1.4;
            }
            
            .table {
              width: 100%;
              border-collapse: separate;
              border-spacing: 0;
              margin: 24px 0;
              background: white;
              border-radius: 8px;
              overflow: hidden;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
              border: 1px solid #e5e7eb;
            }
            
            .table th {
              background: #f9fafb;
              color: #374151;
              padding: 16px 20px;
              text-align: left;
              font-weight: 400;
              font-size: 13px;
              letter-spacing: 0.5px;
              border-bottom: 1px solid #e5e7eb;
            }
            
            .table td {
              padding: 16px 20px;
              border-bottom: 1px solid #f3f4f6;
              font-size: 14px;
              color: #374151;
            }
            
            .table tr:last-child td {
              border-bottom: none;
            }
            
            .table tr:nth-child(even) {
              background: #fafbfc;
            }
            
            .status-chip {
              padding: 6px 12px;
              border-radius: 6px;
              font-size: 12px;
              font-weight: 400;
              display: inline-block;
              background: #f3f4f6;
              color: #374151;
              border: 1px solid #d1d5db;
            }
            
            .variance-text {
              font-weight: 400;
              color: #374151;
            }
            
            .cost-summary {
              background: #f8fafc;
              padding: 32px;
              border-radius: 12px;
              border: 1px solid #e5e7eb;
              margin: 32px 0;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }
            
            .cost-total {
              font-size: 28px;
              font-weight: 400;
              color: #1f2937;
              text-align: center;
              letter-spacing: -0.5px;
            }
            
            .print-info {
              text-align: center;
              color: #9ca3af;
              font-size: 12px;
              margin-top: 48px;
              border-top: 1px solid #e5e7eb;
              padding-top: 24px;
              font-weight: 400;
            }
            
            .two-column {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 32px;
            }
            
            @page { 
              size: auto; 
              margin: 0.3in; 
            }
            @media print {
              body { 
                padding: 10px;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .section { page-break-inside: avoid; }
              .table { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Batch Production Summary</h1>
            <p>Batch Number: ${batch?.batch_number || 'Not Set'}</p>
            <p>Generated: ${new Date().toLocaleString()}</p>
          </div>

          <!-- Basic Information Section -->
          <div class="section">
            <div class="section-title">Basic Information</div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Batch Number</div>
                <div class="info-value">${batch?.batch_number || 'Not Set'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Status</div>
                <div class="info-value">
                  <span class="status-chip">
                    ${batch?.status ? batch.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Pending'}
                  </span>
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">Priority</div>
                <div class="info-value">${batch?.priority?.toUpperCase() || 'NORMAL'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Product</div>
                <div class="info-value">${summaryData.product?.name || 'Not Set'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Formulation</div>
                <div class="info-value">${summaryData.formulation?.name || 'Not Set'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Production Location</div>
                <div class="info-value">${summaryData.location?.name || 'Not Set'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Created Date</div>
                <div class="info-value">${formatDate(batch?.created_at)}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Planned Start</div>
                <div class="info-value">${formatDate(batch?.planned_start_date)}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Planned Completion</div>
                <div class="info-value">${formatDate(batch?.planned_completion_date)}</div>
              </div>
            </div>
          </div>

          <!-- Production Metrics Section -->
          <div class="section">
            <div class="section-title">Production Metrics</div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Target Production Volume</div>
                <div class="info-value">
                  ${actualVolume ? 
                    `${actualVolume.volume.toFixed(2)} ${actualVolume.unit}` :
                    `${(summaryData.formulation?.target_batch_size || 0).toFixed(2)} ${summaryData.formulation?.target_batch_unit || 'gal'}`
                  }
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">Units to Make</div>
                <div class="info-value">${formatNumber(Math.round(batch?.units_to_make || 0))} units</div>
              </div>
              <div class="info-item">
                <div class="info-label">Original Size</div>
                <div class="info-value">${formatNumber(((batch?.units_to_make || 0) * parseFloat((batch?.product_variant || '0').replace(/[^\d.]/g, '') || 0)).toFixed(2))} oz</div>
              </div>
              <div class="info-item">
                <div class="info-label">Density Used</div>
                <div class="info-value">${actualVolume ? `${formatNumber(actualVolume.density.toFixed(3))} g/ml` : '0.950 g/ml'}</div>
              </div>
            </div>
          </div>

          <!-- Ingredients Analysis Section -->
          <div class="section">
            <div class="section-title">Ingredients Analysis</div>
            ${summaryData.batchIngredients.length > 0 ? `
              <table class="table">
                <thead>
                  <tr>
                    <th>Ingredient</th>
                    <th>SKU</th>
                    <th>Planned Amount</th>
                    <th>Actual Amount</th>
                    <th>Variance</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  ${summaryData.batchIngredients.map(ingredient => {
                    let variancePercentage = 0;
                    if (ingredient.planned_amount && ingredient.actual_amount) {
                      const rawMaterial = ingredient.raw_materials;
                      const density = rawMaterial?.density_g_ml || 1.0;
                      const varianceCalc = calculateVariance(
                        ingredient.planned_amount,
                        ingredient.planned_unit,
                        ingredient.actual_amount,
                        ingredient.actual_unit,
                        density
                      );
                      variancePercentage = varianceCalc?.variancePercentage || 0;
                    }
                    
                    return `
                      <tr>
                        <td>${ingredient.raw_materials?.name || 'Unknown'}</td>
                        <td>${ingredient.raw_materials?.sku || 'No SKU'}</td>
                        <td>${formatNumber((ingredient.planned_amount || 0).toFixed(2))} ${ingredient.planned_unit}</td>
                        <td>${ingredient.actual_amount ? formatNumber((ingredient.actual_amount).toFixed(2)) : 'Pending'} ${ingredient.actual_unit || ''}</td>
                        <td class="variance-text">${ingredient.actual_amount ? formatPercentage(variancePercentage) : '-'}</td>
                        <td>
                          <span class="status-chip">
                            ${ingredient.actual_amount ? 'Complete' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    `;
                  }).join('')}
                </tbody>
              </table>
            ` : '<p>No ingredients configured yet.</p>'}
          </div>

          <!-- Cost Analysis Section -->
          <div class="section">
            <div class="section-title">Cost Analysis</div>
            ${summaryData.batchCosts ? `
              <div class="two-column">
                <div>
                  <div class="info-grid">
                    <div class="info-item">
                      <div class="info-label">Material Cost</div>
                      <div class="info-value">${formatCurrency(summaryData.batchCosts.total_ingredient_cost)}</div>
                    </div>
                    <div class="info-item">
                      <div class="info-label">Labor Cost</div>
                      <div class="info-value">${formatCurrency(summaryData.batchCosts.labor_cost)}</div>
                    </div>
                  </div>
                </div>
                <div>
                  <div class="info-grid">
                    <div class="info-item">
                      <div class="info-label">Overhead Cost</div>
                      <div class="info-value">${formatCurrency(summaryData.batchCosts.calculated_overhead_cost || summaryData.batchCosts.overhead_cost)}</div>
                    </div>
                    <div class="info-item">
                      <div class="info-label">Additional Cost</div>
                      <div class="info-value">${formatCurrency(summaryData.batchCosts.additional_cost)}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="cost-summary">
                <div class="cost-total">
                  Total Cost: ${formatCurrency(summaryData.batchCosts.total_cost)}
                </div>
              </div>
            ` : '<p>Cost analysis not yet completed.</p>'}
          </div>

          <!-- Yield Summary Section -->
          <div class="section">
            <div class="section-title">Yield Analysis</div>
            ${summaryData.yieldSummary ? `
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">Target Volume</div>
                  <div class="info-value">${formatNumber((summaryData.yieldSummary.theoretical_yield_kg || 0).toFixed(2))} ${summaryData.yieldSummary.theoretical_yield_unit || 'gal'}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Actual Volume</div>
                  <div class="info-value">${formatNumber((summaryData.yieldSummary.actual_yield_kg || 0).toFixed(2))} ${summaryData.yieldSummary.actual_yield_unit || 'gal'}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Yield Percentage</div>
                  <div class="info-value variance-text">
                    ${formatNumber((summaryData.yieldSummary.yield_percentage || 0).toFixed(2))}%
                  </div>
                </div>
                <div class="info-item">
                  <div class="info-label">Waste Volume</div>
                  <div class="info-value">${formatNumber((summaryData.yieldSummary.waste_amount_kg || 0).toFixed(2))} ${summaryData.yieldSummary.waste_amount_unit || 'gal'}</div>
                </div>
              </div>
            ` : '<p>Yield data not yet available.</p>'}
          </div>

          <!-- Quality Control Section -->
          <div class="section">
            <div class="section-title">Quality Control Status</div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">QC Tests Completed</div>
                <div class="info-value">${summaryData.qcTests.filter(test => test.qa_review_status === 'APPROVED').length}/${summaryData.qcTests.length}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Deviations</div>
                <div class="info-value variance-text">
                  ${summaryData.deviations.length} ${summaryData.deviations.length === 0 ? '(None)' : ''}
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">Signatures Completed</div>
                <div class="info-value">${summaryData.signatures.filter(sig => sig.signer_name).length}/${summaryData.signatures.length}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Batch Log Status</div>
                <div class="info-value">
                  <span class="status-chip">
                    ${summaryData.batchLog?.batch_log_completed ? 'Complete' : 'In Progress'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- QC Tests Details -->
          ${summaryData.qcTests.length > 0 ? `
            <div class="section">
              <div class="section-title">QC Tests Details</div>
              <table class="table">
                <thead>
                  <tr>
                    <th>Test Type</th>
                    <th>Created Date</th>
                    <th>Review Status</th>
                  </tr>
                </thead>
                <tbody>
                  ${summaryData.qcTests.map(test => `
                    <tr>
                      <td>${formatTestType(test.test_type)}</td>
                      <td>${formatDate(test.created_at)}</td>
                      <td>
                        <span class="status-chip">
                          ${test.qa_review_status || 'Pending'}
                        </span>
                      </td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}

          <!-- Deviations Details -->
          ${summaryData.deviations.length > 0 ? `
            <div class="section">
              <div class="section-title">Deviations</div>
              <table class="table">
                <thead>
                  <tr>
                    <th>Deviation Type</th>
                    <th>Description</th>
                    <th>Severity</th>
                    <th>Status</th>
                    <th>Created Date</th>
                  </tr>
                </thead>
                <tbody>
                  ${summaryData.deviations.map(deviation => `
                    <tr>
                      <td>${formatDeviationType(deviation.deviation_type)}</td>
                      <td>${deviation.description || 'No description'}</td>
                      <td>
                        <span class="status-chip">
                          ${deviation.severity || 'LOW'}
                        </span>
                      </td>
                      <td>
                        <span class="status-chip">
                          ${formatDeviationType(deviation.status) || 'Open'}
                        </span>
                      </td>
                      <td>${formatDate(deviation.created_at)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}

          <!-- Signatures Section -->
          ${summaryData.signatures.length > 0 ? `
            <div class="section">
              <div class="section-title">Signatures</div>
              <table class="table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Signer Name</th>
                    <th>Signature Date & Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  ${summaryData.signatures.map(sig => `
                    <tr>
                      <td>${sig.role || 'Unknown Role'}</td>
                      <td>${sig.signer_name || 'Pending'}</td>
                      <td>${formatDateTime(sig.signature_date || sig.signed_at || sig.created_at)}</td>
                      <td>
                        <span class="status-chip">
                          ${sig.signer_name ? 'Signed' : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}

          <div class="print-info">
            <p>Batch Summary Report | Generated on ${new Date().toLocaleString()} | ${batch?.batch_number || 'Production Batch'}</p>
          </div>
        </body>
      </html>
    `;

    // Create a hidden iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.left = '-1000px';
    iframe.style.top = '-1000px';
    
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(printContent);
    iframeDoc.close();
    
    // Wait for content to load then print
    iframe.onload = function() {
      iframe.contentWindow.print();
      // Remove iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  };

  const getVarianceColor = (variance) => {
    if (Math.abs(variance) <= 2) return theme.palette.success.main;
    if (Math.abs(variance) <= 5) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getVarianceIcon = (variance) => {
    if (Math.abs(variance) <= 2) return CheckCircleIcon;
    if (Math.abs(variance) <= 5) return WarningIcon;
    return ErrorIcon;
  };

  const calculateActualProductionVolume = () => {
    if (!batch || !summaryData.formulation || !formulationIngredients.length) {
      return null;
    }
    
    const targetSize = batch.planned_quantity || 0;
    const targetUnit = batch.planned_unit_of_measure || 'oz';
    
    // Calculate weighted average density from formulation ingredients
    let calculatedDensity = 1.0; // Default cosmetic density
    
    if (formulationIngredients.length > 0) {
      let totalWeightedDensity = 0;
      let totalPercentage = 0;
      
      formulationIngredients.forEach(ingredient => {
        const rawMaterial = ingredient.raw_materials;
        const percentage = ingredient.percentage || 0;
        const materialDensity = rawMaterial?.density_g_ml;
        
        if (materialDensity && materialDensity > 0 && percentage > 0) {
          totalWeightedDensity += (materialDensity * percentage);
          totalPercentage += percentage;
        }
      });
      
      if (totalPercentage > 0) {
        calculatedDensity = totalWeightedDensity / totalPercentage;
      }
    }
    
    // Convert to gallons for display
    try {
      const targetUnitType = getUnitType(targetUnit);
      
      if (targetUnitType === 'weight') {
        // Convert weight to volume using density, then to gallons
        const targetInGrams = convertUnits(targetSize, targetUnit, 'g');
        const targetInML = targetInGrams / calculatedDensity;
        const targetInGallons = convertUnits(targetInML, 'ml', 'gal');
        
        return {
          volume: targetInGallons,
          unit: 'gal',
          density: calculatedDensity,
          originalSize: targetSize,
          originalUnit: targetUnit
        };
      } else {
        // Already volume - convert directly to gallons
        const targetInGallons = convertUnits(targetSize, targetUnit, 'gal');
        
        return {
          volume: targetInGallons,
          unit: 'gal',
          density: calculatedDensity,
          originalSize: targetSize,
          originalUnit: targetUnit
        };
      }
    } catch (error) {
      console.warn('Volume conversion failed:', error);
      return null;
    }
  };

  const checkAllStepsCompleted = () => {
    // Check if all previous steps are completed
    const setupCompleted = !!(batch?.id);
    const ingredientsCompleted = !!(batch?.id && summaryData.batchIngredients && summaryData.batchIngredients.length > 0 && 
                                  summaryData.batchIngredients.every(ing => 
                                    ing.planned_amount && 
                                    ing.actual_amount && 
                                    (ing.supplier_lot_number || ing.supplier_batch_number) &&
                                    ing.planned_unit &&
                                    ing.actual_unit
                                  ));
    const costsCompleted = !!(summaryData.batchCosts && summaryData.batchCosts.total_cost > 0);
    const productionCompleted = !!(summaryData.batchLog?.batch_log_completed);

    return setupCompleted && ingredientsCompleted && costsCompleted && productionCompleted;
  };

  const handleApproveBatch = async () => {
    if (!canApproveProductionBatches()) {
      showSnackbar('You do not have permission to approve batches.', 'error');
      return;
    }
    
    if (!batch?.id || !checkAllStepsCompleted()) {
      showSnackbar('Please complete all previous steps before approving the batch', 'error');
      return;
    }
    
    setApprovalDialogOpen(true);
    
    // Only check inventory if we're going to create usage transactions (toggle defaults to true)
    // The check will be re-run when user confirms if toggle is still on
    if (shouldCreateUsageTransactions) {
      setInventoryCheckLoading(true);
      
      try {
        // Fix missing ingredient_id values first (if any)
        await fixMissingIngredientIds(summaryData.loggedIngredients);
        
        // Check inventory availability for logged ingredients
        const { hasEnoughInventory: inventoryAvailable, insufficientItems } = await checkInventoryAvailability(summaryData.loggedIngredients);
        
        setHasEnoughInventory(inventoryAvailable);
        setInsufficientInventoryItems(insufficientItems || []);
        
        if (!inventoryAvailable) {
          console.warn('Insufficient inventory for approval:', insufficientItems);
        }
      } catch (error) {
        console.error('Error checking inventory for approval:', error);
        setHasEnoughInventory(false);
        setInsufficientInventoryItems([]);
      } finally {
        setInventoryCheckLoading(false);
      }
    } else {
      // If toggle is off, set inventory as available since we won't be deducting
      setHasEnoughInventory(true);
      setInsufficientInventoryItems([]);
      setInventoryCheckLoading(false);
    }
  };

  const handleConfirmApproval = async () => {
    if (!approvalSignature.trim()) {
      showSnackbar('Please provide your signature to approve the batch', 'error');
      return;
    }

    setApproving(true);
    try {
      // Ensure batch.id is a string
      const batchId = String(batch.id);
      
      // Fix missing ingredient_id values in logged ingredients (from before schema fix)
      const fixResults = await fixMissingIngredientIds(summaryData.loggedIngredients);
      if (fixResults.success > 0) {
        showSnackbar(`Auto-fixed ${fixResults.success} missing ingredient references`, 'info');
      }
      if (fixResults.failed > 0) {
        console.warn(`Failed to fix ${fixResults.failed} ingredient_id values`);
      }
      
      // Final safety check - re-verify inventory availability ONLY if we're creating usage transactions
      if (shouldCreateUsageTransactions) {
        const { hasEnoughInventory, insufficientItems } = await checkInventoryAvailability(summaryData.loggedIngredients);
        
        if (!hasEnoughInventory) {
          console.error('Inventory changed since dialog opened - insufficient materials:', insufficientItems);
          showSnackbar('Inventory levels changed - insufficient chemicals to approve production. Please close and reopen the approval dialog.', 'error');
          setApproving(false);
          return;
        }
      }

      // Create usage transactions for logged ingredients only if requested
      let usageTransactionCount = 0;
      if (shouldCreateUsageTransactions) {
        const { success: usageSuccess, errors: usageErrors } = await createUsageTransactions(summaryData.loggedIngredients);
        
        if (!usageSuccess) {
          console.error('Usage transaction errors:', usageErrors);
          showSnackbar(`Failed to create usage transactions: ${usageErrors.join(', ')}`, 'error');
          setApproving(false);
          return;
        }
        
        // Count total transactions created
        for (const ingredient of summaryData.loggedIngredients) {
          if (ingredient.ingredient_id && parseFloat(ingredient.actual_quantity) > 0) {
            const lotCount = ingredient.batch_log_ingredient_lots?.length || 1;
            usageTransactionCount += lotCount;
          }
        }
      }
      
      // Update batch status to approved with signature
      const { error: updateError } = await supabase
        .from('production_batches')
        .update({ 
          status: 'completed',
          batch_summary_signature: approvalSignature,
          batch_summary_approved_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', batchId);

      if (updateError) {
        console.error('Error updating batch status:', updateError);
        showSnackbar('Failed to approve batch', 'error');
        setApproving(false);
        return;
      }

      // Add to approval history
      const { error: historyError } = await supabase
        .from('batch_approval_history')
        .insert({
          batch_id: batchId,
          action_type: 'approved',
          signature: approvalSignature,
          reason: null,
          performed_by: (await supabase.auth.getUser()).data.user?.id
        });

      if (historyError) {
        console.warn('Failed to log approval history:', historyError);
      }

      const successMessage = shouldCreateUsageTransactions 
        ? `Batch approved successfully! ${usageTransactionCount} raw material usage transactions created and inventory deducted.`
        : `Batch approved successfully! No usage transactions were created.`;
      
      showSnackbar(successMessage, 'success');
      setApprovalDialogOpen(false);
      setApprovalSignature('');
      setSignaturePreviewName('');
      
      // Refresh data
      fetchApprovalHistory();
      
      // Notify parent component to refresh
      if (onUpdate) {
        const updatedBatch = { 
          ...batch, 
          id: batchId, // Ensure ID is a string
          status: 'completed',
          batch_summary_signature: approvalSignature,
          batch_summary_approved_at: new Date().toISOString()
        };
        onUpdate(updatedBatch);
      }
    } catch (error) {
      console.error('Error approving batch:', error);
      showSnackbar('An error occurred while approving the batch', 'error');
    } finally {
      setApproving(false);
    }
  };

  const handleUnapproveFormulation = () => {
    if (!canApproveProductionBatches()) {
      showSnackbar('You do not have permission to unapprove batches.', 'error');
      return;
    }
    
    setUnapproveDialogOpen(true);
  };

  const handleConfirmUnapproval = async () => {
    if (!canApproveProductionBatches()) {
      showSnackbar('You do not have permission to unapprove batches.', 'error');
      setUnapproveDialogOpen(false);
      return;
    }
    
    if (!unapprovalSignature.trim()) {
      showSnackbar('Please provide your signature to unapprove the batch', 'error');
      return;
    }

    if (!unapprovalReason.trim()) {
      showSnackbar('Please provide a reason for unapproving the batch', 'error');
      return;
    }

    setUnapproving(true);
    try {
      const batchId = String(batch.id);
      
      // Update batch status
      const { error: updateError } = await supabase
        .from('production_batches')
        .update({ 
          status: 'draft',
          batch_summary_signature: null,
          batch_summary_approved_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', batchId);
        
      if (updateError) throw updateError;

      // Add to approval history
      const { error: historyError } = await supabase
        .from('batch_approval_history')
        .insert({
          batch_id: batchId,
          action_type: 'unapproved',
          signature: unapprovalSignature,
          reason: unapprovalReason,
          performed_by: (await supabase.auth.getUser()).data.user?.id
        });

      if (historyError) throw historyError;
      
      showSnackbar('Batch unapproved successfully!', 'success');
      setUnapproveDialogOpen(false);
      setUnapprovalSignature('');
      setUnapprovalReason('');
      setUnapprovalSignaturePreview('');
      
      // Refresh data
      fetchApprovalHistory();
      if (onUpdate) {
        const updatedBatch = { 
          ...batch, 
          id: batchId,
          status: 'draft',
          batch_summary_signature: null,
          batch_summary_approved_at: null
        };
        onUpdate(updatedBatch);
      }
    } catch (error) {
      console.error('Error unapproving batch:', error);
      showSnackbar('Error unapproving batch: ' + error.message, 'error');
    } finally {
      setUnapproving(false);
    }
  };

  // Pagination handlers for approval history
  const handleApprovalPageChange = (event, newPage) => {
    setApprovalPage(newPage);
  };

  const handleApprovalRowsPerPageChange = (event) => {
    setApprovalRowsPerPage(parseInt(event.target.value, 10));
    setApprovalPage(0);
  };

  // Permission check for batch summary
  if (!canAccessPage('batch_summary')) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '70vh',
        textAlign: 'center',
        px: 3
      }}>
        <Card sx={{
          p: 8,
          borderRadius: 3,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: 'white',
          boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
          maxWidth: 600,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          cursor: 'default',
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
            opacity: 0.7,
            transition: 'opacity 0.3s ease'
          },
          '&:hover::before': {
            opacity: 1
          }
        }}>
          <Box sx={{ 
            p: 3, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            mb: 6,
            display: 'inline-flex',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
          }}>
            <SummarizeIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h3" sx={{ 
            mb: 3, 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.5rem',
            letterSpacing: '-0.8px'
          }}>
            Access Restricted
          </Typography>
          <Typography variant="h6" sx={{ 
            color: 'text.secondary', 
            mb: 4,
            fontWeight: 500,
            fontSize: '1.2rem'
          }}>
            Batch Summary View Required
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            lineHeight: 1.7,
            mb: 6,
            fontSize: '1.05rem',
            fontWeight: 500
          }}>
            You don't currently have the necessary permissions to access the Batch Summary. Contact your system administrator to request access or verify your role permissions.
          </Typography>
        </Card>
      </Box>
    );
  }

  if (loading) {
    return (
      <Card sx={{ 
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.2)}`
        }
      }}>
        <CardContent sx={{ p: 6, textAlign: 'center' }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ 
              display: 'inline-flex',
              p: 3,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              mb: 2
            }}>
              <SummarizeIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            </Box>
          </Box>
          <Typography variant="h6" sx={{ 
            fontWeight: 500,
            color: 'primary.main',
            mb: 1,
            fontSize: '1.1rem'
          }}>
            Loading Batch Summary
          </Typography>
          <Typography variant="body2" sx={{ 
            color: 'text.secondary',
            fontWeight: 500,
            maxWidth: 400,
            mx: 'auto',
            lineHeight: 1.6,
            mb: 3
          }}>
            Analyzing batch data, costs, quality metrics, and production logs...
          </Typography>
          <Box sx={{ 
            width: '60%', 
            mx: 'auto',
            '& .MuiLinearProgress-root': {
              height: 8,
              borderRadius: 4,
              bgcolor: alpha(theme.palette.grey[300], 0.3),
              '& .MuiLinearProgress-bar': {
                bgcolor: 'primary.main',
                borderRadius: 4
              }
            }
          }}>
            <LinearProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Enhanced Header Section - Matching BatchOverview.jsx */}
      <Card 
        sx={{ 
          borderRadius: 3,
          mb: 4,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: 'white',
          backdropFilter: 'blur(20px)',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <SummarizeIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                    Batch Summary
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Complete overview of {batch?.batch_number || 'Production Batch'}
                  </Typography>
                </Box>
              </Stack>
            </Box>
            
            <Stack direction="row" spacing={2}>
              {batch?.status !== 'completed' && checkAllStepsCompleted() && canApproveProductionBatches() && (
                <Button
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleApproveBatch}
                  disabled={approving}
                  sx={{
                    borderRadius: 3,
                    px: 3,
                    py: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    color: 'white',
                    fontWeight: 500,
                    boxShadow: `0 6px 18px ${alpha(theme.palette.success.main, 0.25)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.35)}`
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.grey[400], 0.5),
                      color: 'text.disabled'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {approving ? 'Approving...' : 'Approve Batch'}
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrintSummary}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  color: theme.palette.primary.main,
                  fontWeight: 500,
                  background: 'white',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                Print Summary
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Approval Status Display */}
      {batch?.status === 'completed' && batch?.batch_summary_signature && (
        <Card 
          sx={{ 
            borderRadius: 3,
            mb: 4,
            border: `1px solid ${theme.palette.success.main}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.12)}`,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.success.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                background: alpha(theme.palette.success.main, 0.2),
                border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
              }}>
                <CheckCircleIcon sx={{ fontSize: 24, color: theme.palette.success.main }} />
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.success.main, mb: 0.5 }}>
                  Batch Approved Successfully
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  This batch has been completed and approved for release
                </Typography>
                {canApproveProductionBatches() && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleUnapproveFormulation}
                    sx={{
                      mt: 1,
                      borderColor: theme.palette.error.main,
                      color: theme.palette.error.main,
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: theme.palette.error.dark,
                        backgroundColor: alpha(theme.palette.error.main, 0.04)
                      }
                    }}
                  >
                    Unapprove
                  </Button>
                )}
              </Box>

              <Box sx={{ 
                border: `1px solid ${theme.palette.success.main}`, 
                p: 2, 
                borderRadius: 2, 
                bgcolor: alpha(theme.palette.success.main, 0.1),
                minWidth: 200
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                  APPROVED BY:
                </Typography>
                <Box sx={{ 
                  fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                  fontSize: '20px', 
                  color: theme.palette.success.main,
                  textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                  mb: 1
                }}>
                  {batch.batch_summary_signature}
                </Box>
                {batch.batch_summary_approved_at && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Approved: {(() => {
                      const utcDate = new Date(batch.batch_summary_approved_at + (batch.batch_summary_approved_at.includes('Z') ? '' : 'Z'));
                      return utcDate.toLocaleString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: batchTimeFormat === '12hour'
                      });
                    })()}
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Modern Dashboard Grid Layout */}
      <Grid container spacing={3}>
        
        {/* Core Information Row */}
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            height: '100%',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <LocalDrinkIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2, fontSize: '1rem' }}>
                  Batch Info
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                {batch?.batch_number || 'Not set'}
              </Typography>
              <Chip 
                label={batch?.status ? batch.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Pending'}
                sx={{ 
                  fontWeight: 500,
                  borderRadius: 2,
                  background: batch?.status === 'completed' || batch?.status === 'Completed'
                    ? theme.palette.success.main
                    : theme.palette.primary.main,
                  color: 'white',
                  mb: 1
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Priority: {batch?.priority?.toUpperCase() || 'NORMAL'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            height: '100%',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <ScienceIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2, fontSize: '1rem' }}>
                  Production Volume
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                {(() => {
                  const actualVolume = calculateActualProductionVolume();
                  return actualVolume ? 
                    `${formatNumber(actualVolume.volume.toFixed(2))} ${actualVolume.unit}` :
                    `${formatNumber((summaryData.formulation?.target_batch_size || 0).toFixed(2))} ${summaryData.formulation?.target_batch_unit || 'gal'}`;
                })()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Target Volume
              </Typography>
              {batch?.units_to_make && (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {formatNumber(Math.round(batch.units_to_make || 0))} total units
              </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            height: '100%',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <CostIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2, fontSize: '1rem' }}>
                  Total Cost
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                {formatCurrency(summaryData.batchCosts?.total_cost)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                All-in Production Cost
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            height: '100%',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <VerifiedIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2, fontSize: '1rem' }}>
                  Quality Status
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                {summaryData.qcTests.filter(test => test.qa_review_status === 'APPROVED').length}/{summaryData.qcTests.length} Approved
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                QC Tests Complete
              </Typography>
              <Typography variant="body2" sx={{ 
                fontWeight: 500, 
                color: summaryData.deviations.length > 0 ? 'error.main' : 'success.main',
                mt: 1
              }}>
                {summaryData.deviations.length} Deviation(s)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Product Details Row */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            height: '100%',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <AssignmentIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2 }}>
                  Product Details
                </Typography>
              </Box>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Product
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    {summaryData.product?.name || 'Not Set'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Formulation
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    {summaryData.formulation?.name || 'Not Set'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Production Location
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    {summaryData.location?.name || 'Not Set'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            height: '100%',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <ScheduleIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2 }}>
                  Schedule
                </Typography>
              </Box>
              <Stack spacing={2}>
                <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Created Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                  {formatDate(batch?.created_at)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Planned Start
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                  {formatDate(batch?.planned_start_date)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Planned Completion
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                  {formatDate(batch?.planned_completion_date)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Time to Complete Batch
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                  {(() => {
                    const startTime = summaryData.batchLog?.start_time;
                    const endTime = summaryData.yieldSummary?.end_time;
                    
                    if (!startTime || !endTime) {
                      return 'Not available';
                    }
                    
                    const start = dayjs(startTime);
                    const end = dayjs(endTime);
                    const totalMinutes = end.diff(start, 'minute');
                    
                    if (totalMinutes < 0) {
                      return 'Invalid times';
                    }
                    
                    const hours = Math.floor(totalMinutes / 60);
                    const minutes = totalMinutes % 60;
                    
                    if (hours > 0) {
                      return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
                    } else {
                      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
                    }
                  })()}
                </Typography>
              </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Cost Breakdown Cards */}
        {summaryData.batchCosts && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                height: '100%',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: alpha(theme.palette.warning.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
                    }}>
                      <ScienceIcon sx={{ fontSize: 20, color: theme.palette.warning.main }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2, fontSize: '1rem' }}>
                      Material Cost
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                    {formatCurrency(summaryData.batchCosts.total_ingredient_cost)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Chemicals
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                height: '100%',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: alpha(theme.palette.info.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                    }}>
                      <PersonIcon sx={{ fontSize: 20, color: theme.palette.info.main }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2, fontSize: '1rem' }}>
                      Labor Cost
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                    {formatCurrency(summaryData.batchCosts.labor_cost)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Production Labor
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                height: '100%',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: alpha(theme.palette.success.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                    }}>
                      <FactoryIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2, fontSize: '1rem' }}>
                      Overhead
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                    {formatCurrency(summaryData.batchCosts.calculated_overhead_cost || summaryData.batchCosts.overhead_cost)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Facility & Equipment
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                height: '100%',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: alpha(theme.palette.secondary.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`
                    }}>
                      <CostIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.s, ml: 2, fontSize: '1rem' }}>
                      Additional
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                    {formatCurrency(summaryData.batchCosts.additional_cost)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Miscellaneous Costs
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        {/* Yield Analysis Cards */}
        {summaryData.yieldSummary && (
          <>
            <Grid item xs={12} sm={6} lg={3}>
              <Card sx={{ 
                borderRadius: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                height: '100%',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: alpha(summaryData.yieldSummary.yield_percentage >= 95 ? theme.palette.success.main : theme.palette.warning.main, 0.1),
                      border: `1px solid ${alpha(summaryData.yieldSummary.yield_percentage >= 95 ? theme.palette.success.main : theme.palette.warning.main, 0.2)}`
                    }}>
                      <AnalyticsIcon sx={{ fontSize: 20, color: summaryData.yieldSummary.yield_percentage >= 95 ? theme.palette.success.main : theme.palette.warning.main }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2, fontSize: '1rem' }}>
                      Yield %
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: summaryData.yieldSummary.yield_percentage >= 95 ? theme.palette.success.main : theme.palette.warning.main, mb: 1 }}>
                    {formatNumber((summaryData.yieldSummary.yield_percentage || 0).toFixed(1))}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Production Efficiency
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card sx={{ 
                borderRadius: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                height: '100%',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: alpha(theme.palette.primary.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                    }}>
                      <LocalDrinkIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2, fontSize: '1rem' }}>
                      Actual Volume
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                    {formatNumber((summaryData.yieldSummary.actual_yield_kg || 0).toFixed(2))} {summaryData.yieldSummary.actual_yield_unit || 'gal'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Final Product
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card sx={{ 
                borderRadius: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                height: '100%',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: alpha(theme.palette.error.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
                    }}>
                      <ErrorIcon sx={{ fontSize: 20, color: theme.palette.error.main }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2, fontSize: '1rem' }}>
                      Waste Volume
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                    {formatNumber((summaryData.yieldSummary.waste_amount_kg || 0).toFixed(2))} {summaryData.yieldSummary.waste_amount_unit || 'gal'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Lost Material
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Card sx={{ 
                borderRadius: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                height: '100%',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: alpha(summaryData.yieldSummary.right_first_time ? theme.palette.success.main : theme.palette.warning.main, 0.1),
                      border: `1px solid ${alpha(summaryData.yieldSummary.right_first_time ? theme.palette.success.main : theme.palette.warning.main, 0.2)}`
                    }}>
                      <CheckCircleIcon sx={{ fontSize: 20, color: summaryData.yieldSummary.right_first_time ? theme.palette.success.main : theme.palette.warning.main }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, ml: 2, fontSize: '1rem' }}>
                      Right First Time
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: summaryData.yieldSummary.right_first_time ? theme.palette.success.main : theme.palette.warning.main, mb: 1 }}>
                    {summaryData.yieldSummary.right_first_time !== null ? (summaryData.yieldSummary.right_first_time ? 'Yes' : 'No') : 'Not recorded'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Quality Achievement
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        {/* Large Sections Grid */}
        <Grid item xs={12}>
          <Stack spacing={3}>
            {/* Ingredients Analysis */}
            <Accordion 
              expanded={accordionState.ingredients} 
              onChange={(event, expanded) => updateAccordionState('ingredients', expanded)}
              elevation={0}
              sx={{
                borderRadius: '20px !important',
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                overflow: 'hidden',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:before': {
                  display: 'none'
                },
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                '&.Mui-expanded': {
                  margin: '16px 0 16px 0',
                  borderRadius: '20px !important',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                  }
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& .MuiAccordion-region': {
                  borderRadius: '0 0 20px 20px !important'
                },
                '& .MuiAccordionDetails-root': {
                  borderRadius: '0 0 20px 20px !important'
                }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                sx={{
                  borderRadius: accordionState.ingredients ? '20px 20px 0 0 !important' : '20px !important',
                  background: alpha(theme.palette.primary.main, 0.04),
                  border: 'none',
                  minHeight: 72,
                  '&.Mui-expanded': {
                    minHeight: 72,
                    borderRadius: '20px 20px 0 0 !important'
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                    alignItems: 'center',
                    '&.Mui-expanded': {
                      margin: '16px 0'
                    }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <ScienceIcon sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    Ingredients Analysis
                  </Typography>
                  <Chip 
                    label={`${summaryData.batchIngredients.length} ingredients`} 
                    size="small" 
                    sx={{ 
                      ml: 'auto', 
                      mr: 2,
                      fontWeight: 500,
                      borderRadius: 2,
                      background: theme.palette.primary.main,
                      color: 'white'
                    }} 
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 4 }}>
                {summaryData.batchIngredients.length > 0 ? (
                  <Paper sx={{ 
                      borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '@keyframes slideInUp': {
                      '0%': {
                        transform: 'translateY(100%)',
                        opacity: 0,
                      },
                      '100%': {
                        transform: 'translateY(0)',
                        opacity: 1,
                      }
                    },
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    }
                  }}>
                    <TableContainer sx={{ overflow: 'visible' }}>
                      <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                            <TableCell 
                              align="left"
                              sx={{ 
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                bgcolor: 'white',
                                borderBottom: `2px solid ${theme.palette.primary.main}`,
                                color: 'primary.main',
                                py: 2,
                                px: 3,
                                width: 250
                              }}
                            >
                              Ingredient
                            </TableCell>
                            <TableCell 
                              align="center"
                              sx={{ 
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                bgcolor: 'white',
                                borderBottom: `2px solid ${theme.palette.primary.main}`,
                                color: 'primary.main',
                                py: 2,
                                px: 3
                              }}
                            >
                              Planned
                            </TableCell>
                            <TableCell 
                              align="center"
                              sx={{ 
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                bgcolor: 'white',
                                borderBottom: `2px solid ${theme.palette.primary.main}`,
                                color: 'primary.main',
                                py: 2,
                                px: 3
                              }}
                            >
                              Actual
                            </TableCell>
                            <TableCell 
                              align="center"
                              sx={{ 
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                bgcolor: 'white',
                                borderBottom: `2px solid ${theme.palette.primary.main}`,
                                color: 'primary.main',
                                py: 2,
                                px: 3
                              }}
                            >
                              Variance
                            </TableCell>

                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summaryData.batchIngredients.map((ingredient) => {
                          let variancePercentage = 0;
                          if (ingredient.planned_amount && ingredient.actual_amount) {
                            const rawMaterial = ingredient.raw_materials;
                            const density = rawMaterial?.density_g_ml || 1.0;
                            const varianceCalc = calculateVariance(
                              ingredient.planned_amount,
                              ingredient.planned_unit,
                              ingredient.actual_amount,
                              ingredient.actual_unit,
                              density
                            );
                            variancePercentage = varianceCalc?.variancePercentage || 0;
                          }
                          const VarianceIcon = getVarianceIcon(variancePercentage);
                          
                          return (
                              <TableRow 
                                key={ingredient.id}
                                hover
                                sx={{
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    bgcolor: 'grey.50',
                                    transform: 'translateX(4px)',
                                    transition: 'all 0.2s ease',
                                    boxShadow: 'inset 4px 0 0 black'
                                  },
                                  '& .MuiTableCell-root': {
                                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                    py: 2.5,
                                    px: 3
                                  }
                                }}
                              >
                              <TableCell>
                                  <Box>
                                    <Typography 
                                      variant="body1" 
                                      sx={{ 
                                        fontWeight: 500,
                                        fontSize: '0.95rem',
                                        color: theme.palette.text.primary,
                                        mb: 0.5
                                      }}
                                    >
                                  {ingredient.raw_materials?.name}
                                </Typography>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        fontWeight: 500,
                                        color: theme.palette.text.secondary,
                                        fontSize: '0.8rem'
                                      }}
                                    >
                                      {ingredient.raw_materials?.sku || 'No SKU'}
                                </Typography>
                                  </Box>
                              </TableCell>
                                <TableCell align="center">
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: 500,
                                      fontSize: '0.9rem',
                                      color: theme.palette.text.primary
                                    }}
                                  >
                                {formatNumber((ingredient.planned_amount || 0).toFixed(2))} {ingredient.planned_unit}
                                  </Typography>
                              </TableCell>
                                <TableCell align="center">
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: 500,
                                      fontSize: '0.9rem',
                                      color: theme.palette.text.primary
                                    }}
                                  >
                                {ingredient.actual_amount ? formatNumber((ingredient.actual_amount).toFixed(2)) : 'Pending'} {ingredient.actual_unit || ''}
                                  </Typography>
                              </TableCell>
                                <TableCell align="center">
                                {ingredient.actual_amount ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                                    <VarianceIcon 
                                      sx={{ 
                                        fontSize: 16, 
                                        color: getVarianceColor(variancePercentage) 
                                      }} 
                                    />
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        color: getVarianceColor(variancePercentage),
                                          fontWeight: 500,
                                          fontSize: '0.9rem'
                                      }}
                                    >
                                      {formatPercentage(variancePercentage)}
                                    </Typography>
                                  </Box>
                                ) : (
                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: theme.palette.text.secondary }}>-</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  </Paper>
                ) : (
                  <Alert severity="info">No ingredients configured yet</Alert>
                )}
              </AccordionDetails>
            </Accordion>

            {/* QC Tests Summary */}
            <Accordion 
              expanded={accordionState.qcTests} 
              onChange={(event, expanded) => updateAccordionState('qcTests', expanded)}
              elevation={0}
              sx={{
                borderRadius: '20px !important',
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                overflow: 'hidden',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:before': {
                  display: 'none'
                },
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                '&.Mui-expanded': {
                  margin: '16px 0 16px 0',
                  borderRadius: '20px !important',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                  }
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& .MuiAccordion-region': {
                  borderRadius: '0 0 20px 20px !important'
                },
                '& .MuiAccordionDetails-root': {
                  borderRadius: '0 0 20px 20px !important'
                }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                sx={{
                  borderRadius: accordionState.qcTests ? '20px 20px 0 0 !important' : '20px !important',
                  background: alpha(theme.palette.primary.main, 0.04),
                  border: 'none',
                  minHeight: 72,
                  '&.Mui-expanded': {
                    minHeight: 72,
                    borderRadius: '20px 20px 0 0 !important'
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                    alignItems: 'center',
                    '&.Mui-expanded': {
                      margin: '16px 0'
                    }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <VerifiedIcon sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    QC Tests
                  </Typography>
                  <Chip 
                    label={`${summaryData.qcTests.filter(test => test.qa_review_status === 'APPROVED').length}/${summaryData.qcTests.length} approved`} 
                    size="small" 
                    sx={{ 
                      ml: 'auto', 
                      mr: 2,
                      fontWeight: 500,
                      borderRadius: 2,
                      background: theme.palette.success.main,
                      color: 'white'
                    }} 
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 4 }}>
                {summaryData.qcTests.length > 0 ? (
                  <Stack spacing={2}>
                    {summaryData.qcTests.map((test, index) => (
                      <Paper key={index} sx={{ 
                        p: 2, 
                        background: `white`,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                          {formatTestType(test.test_type)}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(test.created_at)}
                          </Typography>
                          <Chip 
                            label={test.qa_review_status || 'Pending'}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              borderRadius: 2,
                              fontSize: '0.75rem',
                              backgroundColor: test.qa_review_status === 'APPROVED'
                                ? alpha(theme.palette.success.main, 0.1)
                                : alpha(theme.palette.warning.main, 0.1),
                              color: test.qa_review_status === 'APPROVED' ? theme.palette.success.main : theme.palette.warning.main,
                              border: `1px solid ${test.qa_review_status === 'APPROVED' ? theme.palette.success.main : theme.palette.warning.main}`,
                            }}
                          />
                        </Box>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info">No QC tests configured yet</Alert>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Deviations Summary */}
            <Accordion 
              expanded={accordionState.deviations} 
              onChange={(event, expanded) => updateAccordionState('deviations', expanded)}
              elevation={0}
              sx={{
                borderRadius: '20px !important',
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                overflow: 'hidden',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:before': {
                  display: 'none'
                },
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                '&.Mui-expanded': {
                  margin: '16px 0 16px 0',
                  borderRadius: '20px !important',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                  }
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& .MuiAccordion-region': {
                  borderRadius: '0 0 20px 20px !important'
                },
                '& .MuiAccordionDetails-root': {
                  borderRadius: '0 0 20px 20px !important'
                }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                sx={{
                  borderRadius: accordionState.deviations ? '20px 20px 0 0 !important' : '20px !important',
                  background: alpha(theme.palette.error.main, 0.04),
                  border: 'none',
                  minHeight: 72,
                  '&.Mui-expanded': {
                    minHeight: 72,
                    borderRadius: '20px 20px 0 0 !important'
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                    alignItems: 'center',
                    '&.Mui-expanded': {
                      margin: '16px 0'
                    }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <ErrorIcon sx={{ color: theme.palette.error.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    Deviations
                  </Typography>
                  <Chip 
                    label={`${summaryData.deviations.length} deviation${summaryData.deviations.length !== 1 ? 's' : ''}`} 
                    size="small" 
                    sx={{ 
                      ml: 'auto', 
                      mr: 2,
                      fontWeight: 500, 
                      borderRadius: 2,
                      background: summaryData.deviations.length > 0 ? theme.palette.error.main : theme.palette.success.main,
                      color: 'white'
                    }} 
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 4 }}>
                {summaryData.deviations.length > 0 ? (
                  <Stack spacing={2}>
                    {summaryData.deviations.map((deviation, index) => (
                      <Paper key={index} sx={{ 
                        p: 2, 
                        background: `white`,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                          {formatDeviationType(deviation.deviation_type)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {deviation.description || 'No description'}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip 
                            label={deviation.severity || 'LOW'}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              borderRadius: 2,
                              fontSize: '0.75rem',
                              backgroundColor: 
                                deviation.severity === 'HIGH' ? alpha(theme.palette.error.main, 0.1) :
                                deviation.severity === 'MEDIUM' ? alpha(theme.palette.warning.main, 0.1) :
                                alpha(theme.palette.success.main, 0.1),
                              color: 
                                deviation.severity === 'HIGH' ? theme.palette.error.main :
                                deviation.severity === 'MEDIUM' ? theme.palette.warning.main :
                                theme.palette.success.main,
                              border: `1px solid ${
                                deviation.severity === 'HIGH' ? theme.palette.error.main :
                                deviation.severity === 'MEDIUM' ? theme.palette.warning.main :
                                theme.palette.success.main
                              }`,
                            }}
                          />
                          <Chip 
                            label={formatDeviationType(deviation.status) || 'Open'}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              borderRadius: 2,
                              fontSize: '0.75rem',
                              backgroundColor: (deviation.status || '').toLowerCase().includes('closed') 
                                ? alpha(theme.palette.success.main, 0.1)
                                : alpha(theme.palette.warning.main, 0.1),
                              color: (deviation.status || '').toLowerCase().includes('closed') 
                                ? theme.palette.success.main 
                                : theme.palette.warning.main,
                              border: `1px solid ${(deviation.status || '').toLowerCase().includes('closed') 
                                ? theme.palette.success.main 
                                : theme.palette.warning.main}`,
                            }}
                          />
                        </Box>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="success">No deviations reported</Alert>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Approval History */}
            <Accordion 
              expanded={accordionState.approvalHistory} 
              onChange={(event, expanded) => updateAccordionState('approvalHistory', expanded)}
              elevation={0}
              sx={{
                borderRadius: '20px !important',
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                overflow: 'hidden',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:before': {
                  display: 'none'
                },
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                '&.Mui-expanded': {
                  margin: '16px 0 16px 0',
                  borderRadius: '20px !important',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                  }
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& .MuiAccordion-region': {
                  borderRadius: '0 0 20px 20px !important'
                },
                '& .MuiAccordionDetails-root': {
                  borderRadius: '0 0 20px 20px !important'
                }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                sx={{
                  borderRadius: accordionState.approvalHistory ? '20px 20px 0 0 !important' : '20px !important',
                  background: alpha(theme.palette.primary.main, 0.04),
                  border: 'none',
                  minHeight: 72,
                  '&.Mui-expanded': {
                    minHeight: 72,
                    borderRadius: '20px 20px 0 0 !important'
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                    alignItems: 'center',
                    '&.Mui-expanded': {
                      margin: '16px 0'
                    }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <AssignmentIcon sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    Approval History
                  </Typography>
                  <Chip 
                    label={`${approvalHistory.length} entries`} 
                    size="small" 
                    sx={{ 
                      ml: 'auto', 
                      mr: 2,
                      fontWeight: 500,
                      borderRadius: 2,
                      background: theme.palette.primary.main,
                      color: 'white'
                    }} 
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 4 }}>
                {approvalHistory.length > 0 ? (
                  <Stack spacing={2}>
                    {(approvalRowsPerPage > 0
                      ? approvalHistory.slice(approvalPage * approvalRowsPerPage, approvalPage * approvalRowsPerPage + approvalRowsPerPage)
                      : approvalHistory
                    ).map((entry, index) => (
                      <Paper key={entry.id} sx={{ 
                        p: 3, 
                        background: entry.action_type === 'approved' 
                          ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)}, ${alpha(theme.palette.success.main, 0.02)})`
                          : `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.05)}, ${alpha(theme.palette.error.main, 0.02)})`,
                        borderRadius: 2,
                        border: `1px solid ${entry.action_type === 'approved' ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.error.main, 0.2)}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}`
                      }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip 
                                label={entry.action_type === 'approved' ? 'APPROVED' : 'UNAPPROVED'}
                                size="small"
                                sx={{
                                  fontWeight: 500,
                                  borderRadius: 2,
                                  fontSize: '0.75rem',
                                  backgroundColor: entry.action_type === 'approved'
                                    ? alpha(theme.palette.success.main, 0.1)
                                    : alpha(theme.palette.error.main, 0.1),
                                  color: entry.action_type === 'approved' ? theme.palette.success.main : theme.palette.error.main,
                                  border: `1px solid ${entry.action_type === 'approved' ? theme.palette.success.main : theme.palette.error.main}`,
                                }}
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Signature
                            </Typography>
                            <Box sx={{ 
                              fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                              fontSize: '18px', 
                              color: entry.action_type === 'approved' ? theme.palette.success.main : theme.palette.error.main,
                              textShadow: '1px 1px 1px rgba(0,0,0,0.1)'
                            }}>
                              {entry.signature}
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Performed By
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {entry.profiles?.full_name 
                                ? entry.profiles.full_name
                                : entry.profiles?.first_name && entry.profiles?.last_name 
                                ? `${entry.profiles.first_name} ${entry.profiles.last_name}`
                                : entry.profiles?.first_name
                                ? entry.profiles.first_name
                                : entry.profiles?.last_name
                                ? entry.profiles.last_name
                                : entry.profiles?.email
                                ? entry.profiles.email.split('@')[0]
                                : entry.performed_by 
                                ? `User (${entry.performed_by.slice(0, 8)}...)`
                                : 'Unknown User'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {entry.profiles?.email || 'No email available'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Date & Time
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {(() => {
                                const utcDate = new Date(entry.performed_at + (entry.performed_at.includes('Z') ? '' : 'Z'));
                                return utcDate.toLocaleString('en-US', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: batchTimeFormat === '12hour'
                                });
                              })()}
                            </Typography>
                          </Grid>
                          {entry.reason && (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
                                Reason
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500,
                                p: 2,
                                backgroundColor: alpha(theme.palette.grey[100], 0.5),
                                borderRadius: 1,
                                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                              }}>
                                {entry.reason}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <InfoIcon sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.grey[600], mb: 1 }}>
                      No Approval History
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This batch has not been approved or unapproved yet
                    </Typography>
                  </Box>
                )}
                
                {/* Pagination for Approval History */}
                {approvalHistory.length > 0 && (
                  <Box sx={{ 
                    borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    borderRadius: '0 0 12px 12px',
                    mt: 2
                  }}>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={approvalHistory.length}
                      rowsPerPage={approvalRowsPerPage}
                      page={approvalPage}
                      onPageChange={handleApprovalPageChange}
                      onRowsPerPageChange={handleApprovalRowsPerPageChange}
                      sx={{ 
                        px: 3,
                        py: 2,
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          color: theme.palette.text.primary
                        },
                        '& .MuiTablePagination-select': {
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          borderRadius: 2,
                          px: 2,
                          py: 1,
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          background: 'white',
                          '&:hover': {
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          }
                        },
                        '& .MuiTablePagination-actions': {
                          '& .MuiIconButton-root': {
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            borderRadius: 2,
                            margin: '0 2px',
                            background: 'white',
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              background: theme.palette.primary.main,
                              color: 'white',
                              transform: 'translateY(-1px)',
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                            },
                            '&.Mui-disabled': {
                              background: alpha(theme.palette.grey[300], 0.5),
                              color: theme.palette.grey[500],
                              border: `1px solid ${theme.palette.grey[300]}`,
                              transform: 'none',
                              boxShadow: 'none'
                            }
                          }
                        }
                      }}
                    />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Grid>
      </Grid>

      {/* Approval Confirmation Dialog */}
      <Dialog 
        open={approvalDialogOpen} 
        onClose={() => setApprovalDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon sx={{ mr: 2 }} />
            Confirm Batch Approval
          </Box>
          <IconButton 
            onClick={() => setApprovalDialogOpen(false)} 
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                transform: 'scale(1.1)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ mb: 3, borderRadius: 2, mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                  Are you sure you want to approve this batch?
                </Typography>
                <Typography variant="body2">
                  This action will complete the batch and mark it as approved for release. 
                  This cannot be undone.
                </Typography>
              </Alert>
            </Grid>

            {/* Usage Transaction Option */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, border: `1px solid ${theme.palette.warning.main}`, borderRadius: 2, backgroundColor: alpha(theme.palette.warning.main, 0.05) }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={shouldCreateUsageTransactions}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        setShouldCreateUsageTransactions(newValue);
                        
                        // If turning ON, check inventory availability
                        if (newValue) {
                          setInventoryCheckLoading(true);
                          try {
                            const { hasEnoughInventory: inventoryAvailable, insufficientItems } = await checkInventoryAvailability(summaryData.loggedIngredients);
                            setHasEnoughInventory(inventoryAvailable);
                            setInsufficientInventoryItems(insufficientItems || []);
                          } catch (error) {
                            console.error('Error checking inventory:', error);
                            setHasEnoughInventory(false);
                            setInsufficientInventoryItems([]);
                          } finally {
                            setInventoryCheckLoading(false);
                          }
                        } else {
                          // If turning OFF, allow approval (no inventory check needed)
                          setHasEnoughInventory(true);
                          setInsufficientInventoryItems([]);
                        }
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: theme.palette.primary.main,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: theme.palette.primary.main,
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                        Create Usage Transactions & Deduct Inventory
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        Turn off if this batch was already produced and you're re-approving after unapproval
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            </Grid>

            {/* Chemicals Usage Summary */}
            {summaryData.loggedIngredients && summaryData.loggedIngredients.length > 0 && shouldCreateUsageTransactions && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, border: `1px solid ${theme.palette.primary.main}`, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2, color: 'primary.main' }}>
                    Chemicals to be Deducted from Inventory:
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflowY: 'auto', pr: 2 }}>
                    {summaryData.loggedIngredients.map((ingredient, index) => {
                      const materialName = ingredient.ingredient_name || 'Unknown Material';
                      const quantity = parseFloat(ingredient.actual_quantity) || 0;
                      // Use raw material's primary_unit_measure if available, otherwise fallback to ingredient's unit
                      const rawMaterialUnit = ingredient.raw_materials?.primary_unit_measure;
                      const unit = rawMaterialUnit || ingredient.unit || '';
                      
                      if (quantity <= 0) return null;
                      
                      // Get lots for this ingredient
                      const ingredientLots = ingredient.batch_log_ingredient_lots || [];
                      
                      return (
                        <Box key={index} sx={{ 
                          py: 1,
                          borderBottom: index < summaryData.loggedIngredients.length - 1 ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            mb: ingredientLots.length > 0 ? 0.5 : 0
                          }}>
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                              {materialName}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                              -{formatNumber(quantity.toFixed(3))} {unit} {ingredientLots.length > 0 ? 'total' : ''}
                            </Typography>
                          </Box>
                          {ingredientLots.length > 0 && (
                            <Box sx={{ pl: 2, mt: 0.5 }}>
                              {ingredientLots.map((lot, lotIndex) => {
                                const lotQuantity = parseFloat(lot.quantity_used) || 0;
                                // Use raw material's primary_unit_measure if available, otherwise fallback to lot's unit_used or ingredient's unit
                                const rawMaterialUnit = ingredient.raw_materials?.primary_unit_measure;
                                const lotUnit = rawMaterialUnit || lot.unit_used || unit;
                                const lotNumber = lot.lot_number || lot.batch_number || 'Unknown';
                                const batchNumber = lot.batch_number;
                                
                                if (lotQuantity <= 0) return null;
                                
                                return (
                                  <Box key={lotIndex} sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    py: 0.25,
                                    fontSize: '0.75rem'
                                  }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      • Lot: {lotNumber}{batchNumber && batchNumber !== lotNumber ? `, Batch: ${batchNumber}` : ''}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'primary.main', ml: 2 }}>
                                      -{formatNumber(lotQuantity.toFixed(3))} {lotUnit}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                        </Box>
                      );
                    }).filter(Boolean)}
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                    These quantities will be recorded as usage transactions and deducted from inventory at the production location.
                  </Typography>
                </Paper>
              </Grid>
            )}
            
            {/* Inventory Check Status */}
            {inventoryCheckLoading && shouldCreateUsageTransactions && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2">
                    Checking chemical inventory availability...
                  </Typography>
                </Alert>
              </Grid>
            )}
            
            {!inventoryCheckLoading && !hasEnoughInventory && insufficientInventoryItems.length > 0 && shouldCreateUsageTransactions && (
              <Grid item xs={12}>
                <Paper sx={{ 
                  p: 2, 
                  border: `1px solid ${theme.palette.error.main}`, 
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.error.main, 0.05)
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2, color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon sx={{ fontSize: '1.1rem' }} />
                    Insufficient Chemicals Inventory
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.primary' }}>
                    Cannot approve batch - the following ingredients don't have enough inventory:
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflowY: 'auto', pr: 2 }}>
                    {insufficientInventoryItems.map((item, index) => (
                      <Box key={index} sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        py: 0.5,
                        borderBottom: index < insufficientInventoryItems.length - 1 ? `1px solid ${alpha(theme.palette.error.main, 0.2)}` : 'none'
                      }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ color: 'text.primary' }}>
                            {item.materialName}
                          </Typography>
                          {item.lotInfo && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                              {item.lotInfo}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
                            Need: {formatNumber(item.requiredQuantity.toFixed(3))} {item.unit}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Have: {formatNumber(item.availableQuantity.toFixed(3))} {item.unit}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.dark' }}>
                            Short: {formatNumber(item.shortage.toFixed(3))} {item.unit}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                    Please restock inventory or reduce ingredient quantities to proceed with approval.
                  </Typography>
                </Paper>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                Enter your name to confirm approval:
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Approver Name"
                fullWidth
                autoFocus
                required
                value={approvalSignature}
                onChange={(e) => {
                  setApprovalSignature(e.target.value);
                  setSignaturePreviewName(e.target.value);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                mt: 2, 
                p: 3, 
                border: `1px solid ${theme.palette.grey[400]}`, 
                borderRadius: 2, 
                backgroundColor: alpha(theme.palette.grey[50], 0.8)
              }}>
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                  Preview: Your signature will appear in script font
                </Typography>
                <Box sx={{ 
                  fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                  fontSize: '28px', 
                  color: theme.palette.grey[800],
                  mt: 2,
                  mb: 1,
                  textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {signaturePreviewName || 'Preview will appear here'}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
          <Button 
            onClick={() => setApprovalDialogOpen(false)}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              color: theme.palette.grey[600],
              '&:hover': {
                backgroundColor: alpha(theme.palette.grey[200], 0.5)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmApproval} 
            variant="contained"
            disabled={approving || !approvalSignature.trim() || (shouldCreateUsageTransactions && (inventoryCheckLoading || !hasEnoughInventory))}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1,
              background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                transform: 'translateY(-1px)',
                boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.4)}`
              },
              '&:disabled': {
                background: alpha(theme.palette.grey[400], 0.5),
                color: 'text.disabled'
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {approving ? 'Approving...' : 
             (shouldCreateUsageTransactions && inventoryCheckLoading) ? 'Checking Inventory...' :
             (shouldCreateUsageTransactions && !hasEnoughInventory) ? 'Insufficient Inventory' :
             'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unapprove Dialog */}
      <Dialog
        open={unapproveDialogOpen}
        onClose={() => setUnapproveDialogOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[300]}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 3, 
          borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon sx={{ mr: 2, color: theme.palette.error.main }} />
            Unapprove Batch
          </Box>
          <IconButton 
            onClick={() => setUnapproveDialogOpen(false)} 
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                transform: 'scale(1.1)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2, mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1, color: '#000' }}>
                  Are you sure you want to unapprove this batch?
                </Typography>
                <Typography variant="body2" sx={{ color: '#000' }}>
                  This will mark the batch as draft and require re-approval. This action will be logged in the approval history.
                </Typography>
              </Alert>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                Reason for unapproval (required):
              </Typography>
              <TextField
                label="Reason"
                fullWidth
                multiline
                rows={3}
                required
                value={unapprovalReason}
                onChange={(e) => setUnapprovalReason(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 },
                    backgroundColor: 'white'
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                Enter your name to confirm unapproval:
              </Typography>
              <TextField
                label="Approver Name"
                fullWidth
                required
                value={unapprovalSignature}
                onChange={(e) => {
                  setUnapprovalSignature(e.target.value);
                  setUnapprovalSignaturePreview(e.target.value);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 },
                    backgroundColor: 'white'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                mt: 2, 
                p: 3, 
                border: `1px solid ${theme.palette.grey[400]}`, 
                borderRadius: 2, 
                backgroundColor: 'white'
              }}>
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                  Preview: Your signature will appear in script font
                </Typography>
                <Box sx={{ 
                  fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                  fontSize: '28px', 
                  color: theme.palette.grey[800],
                  mt: 2,
                  mb: 1,
                  textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {unapprovalSignaturePreview || 'Preview will appear here'}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`
        }}>
          <Button 
            onClick={() => setUnapproveDialogOpen(false)}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              color: theme.palette.grey[600],
              '&:hover': {
                backgroundColor: alpha(theme.palette.grey[200], 0.5)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmUnapproval} 
            variant="contained"
            disabled={unapproving || !unapprovalSignature.trim() || !unapprovalReason.trim()}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1,
              background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.error.dark}, ${theme.palette.error.main})`,
                transform: 'translateY(-1px)',
                boxShadow: `0 6px 20px ${alpha(theme.palette.error.main, 0.4)}`
              },
              '&:disabled': {
                background: alpha(theme.palette.grey[400], 0.5),
                color: 'text.disabled'
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {unapproving ? 'Unapproving...' : 'Confirm Unapproval'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}